/**
 * Sentry init — React client side.
 *
 * Per CHAP-264. Mirrors the Node init in `server/lib/sentry.ts` but uses
 * the React SDK with Browser-specific integrations.
 *
 * DSN is exposed to the client via the `VITE_SENTRY_DSN` env var (Vite
 * inlines `VITE_*` vars at build time; never expose secret-server-side
 * tokens this way). The Sentry DSN itself is safe to expose — that's
 * its design.
 *
 * No-op when VITE_SENTRY_DSN is unset (dev without DSN, PR previews
 * pre-credential).
 */

import * as Sentry from '@sentry/react';

let _initialized = false;

export function initSentry(): void {
  if (_initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info('[sentry] VITE_SENTRY_DSN not set; client error reporting disabled');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GIT_COMMIT,
    // 10% trace + 100% errors in production; full in dev
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Session replays are off v1 — donor data sensitivity; enable per-tenant
    // later if a Cabinet member explicitly requests UX-issue investigation
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [Sentry.browserTracingIntegration()],
    // PII safety — never send IPs / user-agents by default; the
    // setUser/setTag calls below send only what we explicitly pass.
    sendDefaultPii: false,
    initialScope: {
      tags: {
        service: 'chapterbuild-web-client',
        brand: 'chapterbuild',
      },
    },
  });

  _initialized = true;
}

/** Annotate the client-side scope with tenant/user identity after login. */
export function annotateClientScope(opts: {
  tenantId?: string;
  tenantSlug?: string;
  userId?: string;
  userEmail?: string;
  role?: string;
  tier?: string | null;
}): void {
  if (!_initialized) return;
  Sentry.setUser({
    id: opts.userId,
    email: opts.userEmail,
  });
  Sentry.setTag('tenant_id', opts.tenantId ?? 'none');
  Sentry.setTag('tenant_slug', opts.tenantSlug ?? 'none');
  Sentry.setTag('role', opts.role ?? 'anonymous');
  Sentry.setTag('tier', opts.tier ?? 'none');
}

/**
 * React ErrorBoundary wrapper from the Sentry SDK. Use at the App shell
 * level + around any high-blast-radius subtree.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export { Sentry };
